import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { rememberHubReturn } from 'utils/hubReturnPath'
import styled, { keyframes } from 'styled-components'
import AddIcon from '@mui/icons-material/Add'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import useDebounce from 'utils/hooks/useDebounce'
import {
  selectCurrentIncident,
  getIncidentsRoutine,
  getIncidentStatsRoutine,
  selectIncidents,
  selectIncidentsLoading,
  selectIncidentsMeta,
  selectIncidentStats,
  selectSuggestions,
  selectIncidentMutationLoading,
  clearIncidentSuggestions,
} from 'features/incidents/ducks'
import { getUserBps } from 'features/bpList/ducks/selectors'
import IncidentsAPI from 'services/IncidentsAPI'
import { toast } from 'react-toastify'
import HubScopeFilter from 'features/taskManagement/components/HubScopeFilter'
import IncidentForm from './components/IncidentForm'
import IncidentReviewStep from './components/IncidentReviewStep'
import IncidentRow from './components/IncidentRow'
import IncidentDetailDrawer from './components/IncidentDetailDrawer'
import GuruTip from 'features/guru/GuruTip'

const SEVERITY_META = {
  critical: { label: 'Critical', colour: '#b00020', bg: '#fdecea' },
  high:     { label: 'High',     colour: '#c2410c', bg: '#ffedd5' },
  medium:   { label: 'Medium',   colour: '#d97706', bg: '#fef3c7' },
  low:      { label: 'Low',      colour: '#15803d', bg: '#dcfce7' },
}

const STATUS_META = {
  open:     { label: 'Open' },
  resolved: { label: 'Resolved' },
  closed:   { label: 'Closed' },
}

const TYPE_OPTIONS = [
  { value: null,        label: 'All types' },
  { value: 'incident',  label: 'Incidents' },
  { value: 'accident',  label: 'Accidents' },
  { value: 'near_miss', label: 'Near misses' },
]

const IncidentsPage = ({ buildingPassportId, portfolioId, estateId, availableBPs }) => {
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const returnBp = useMemo(() => new URLSearchParams(window.location.search).get('from'), [])
  useEffect(() => {
    rememberHubReturn(location.pathname + location.search)
  }, [location.pathname, location.search])
  const incidents = useSelector(selectIncidents)
  const loading = useSelector(selectIncidentsLoading)
  const meta = useSelector(selectIncidentsMeta)
  const stats = useSelector(selectIncidentStats)
  const suggestions = useSelector(selectSuggestions)
  const userBps = useSelector(getUserBps)

  // When availableBPs isn't supplied (global view), derive it from the user's BP list,
  // filtered to those with Task Management enabled.
  const effectiveAvailableBPs = useMemo(() => {
    if (Array.isArray(availableBPs) && availableBPs.length) return availableBPs
    return (userBps || [])
      .filter(bp => bp.hasTaskManagement)
      .map(bp => ({ id: bp.id, name: bp.name || bp.address || `Building Passport ${bp.id?.slice(0, 8)}` }))
  }, [availableBPs, userBps])

  const [mode, setMode] = useState('list') // 'list' | 'new' | 'review'
  const [pendingForm, setPendingForm] = useState(null)
  const drawerIncidentId = searchParams.get('open')
  const pushedOpenRef = useRef(false)
  const setDrawerIncidentId = useCallback(id => {
    const next = new URLSearchParams(searchParams)
    if (id) {
      pushedOpenRef.current = true
      next.set('open', id)
      setSearchParams(next)
      return
    }
    if (pushedOpenRef.current && window.history.length > 1) {
      pushedOpenRef.current = false
      navigate(-1)
      return
    }
    next.delete('open')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, navigate])

  const status = searchParams.get('status') || 'open'
  const severity = searchParams.get('severity') || null
  const incidentType = searchParams.get('type') || null
  const riddor = searchParams.get('riddor') || null
  const mor = searchParams.get('mor') || null
  const investigation = searchParams.get('investigation') || null
  const urlSearch = searchParams.get('q') || ''

  const [searchInput, setSearchInput] = useState(urlSearch)
  const debouncedSearch = useDebounce(searchInput, 300)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (debouncedSearch) next.set('q', debouncedSearch)
    else next.delete('q')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line
  }, [debouncedSearch])

  const filters = useMemo(() => ({
    building_passport_id: buildingPassportId || searchParams.get('bp'),
    portfolio_id: portfolioId || searchParams.get('portfolio'),
    estate_id: estateId || searchParams.get('estate'),
    search: debouncedSearch || null,
    severity,
    status,
    incident_type: incidentType,
    riddor,
    mor,
    investigation,
  }), [buildingPassportId, portfolioId, estateId, searchParams, severity, status, debouncedSearch, incidentType, riddor, mor, investigation])

  const buildParams = useCallback((page = 1) => {
    const p = { page, per_page: 25 }
    Object.entries(filters || {}).forEach(([k, v]) => { if (v) p[`filter[${k}]`] = v })
    return p
  }, [filters])

  const statsParams = useMemo(() => ({
    ...(filters.building_passport_id ? { building_passport_id: filters.building_passport_id } : {}),
    ...(filters.portfolio_id ? { portfolio_id: filters.portfolio_id } : {}),
    ...(filters.estate_id ? { estate_id: filters.estate_id } : {}),
  }), [filters.building_passport_id, filters.portfolio_id, filters.estate_id])

  // Fetch page 1 whenever filters change (reducer detects page === 1 and replaces).
  useEffect(() => { dispatch(getIncidentsRoutine(buildParams(1))) }, [dispatch, buildParams])
  useEffect(() => { dispatch(getIncidentStatsRoutine(statsParams)) }, [dispatch, statsParams])

  // Auto-refresh after a mutation. The reducer patches the touched row in
  // place but stats counters stay stale and a status-changed row would
  // linger in a status-filtered list. Refetch list + stats whenever a
  // mutation completes (true → false transition).
  const mutationLoading = useSelector(selectIncidentMutationLoading)
  const prevMutationLoading = useRef(false)
  useEffect(() => {
    if (prevMutationLoading.current && !mutationLoading) {
      dispatch(getIncidentsRoutine(buildParams(1)))
      dispatch(getIncidentStatsRoutine(statsParams))
    }
    prevMutationLoading.current = mutationLoading
  }, [mutationLoading, dispatch, buildParams, statsParams])

  // userBps mount-fetch removed — HubScopeFilter handles it now (and only
  // when the slice is actually empty).

  const hasMore = meta && meta.currentPage < meta.lastPage
  const loadMore = () => {
    if (!hasMore || loading) return
    dispatch(getIncidentsRoutine(buildParams(meta.currentPage + 1)))
  }

  // When the user submits the form and suggestions arrive, switch to review.
  useEffect(() => {
    if (mode === 'new' && suggestions) setMode('review')
  }, [suggestions, mode])

  const currentIncident = useSelector(selectCurrentIncident)
  const incidentBeforeReview = useRef(null)
  useEffect(() => {
    if (mode === 'review') incidentBeforeReview.current = incidentBeforeReview.current ?? (currentIncident?.id || 'none')
  }, [mode, currentIncident])

  useEffect(() => {
    if (mode === 'review' && !suggestions) {
      setMode('list')
      const createdId = currentIncident?.id
      if (createdId && createdId !== incidentBeforeReview.current) setDrawerIncidentId(createdId)
      incidentBeforeReview.current = null
    }
  }, [suggestions, mode, currentIncident, setDrawerIncidentId])

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value === null || value === undefined) next.delete(key)
    else next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  const setStatus = (s) => updateParam('status', s)
  const toggleSeverity = (s) => updateParam('severity', severity === s ? null : s)

  const canReport = Boolean(filters.building_passport_id) || effectiveAvailableBPs.length > 0

  const goBackToForm = () => {
    // Clear suggestions FIRST so the auto-flip-to-review effect doesn't bounce us back.
    dispatch(clearIncidentSuggestions())
    setMode('new')
  }

  const startNewIncident = () => {
    // Clear any stale suggestions from a previous abandoned report.
    dispatch(clearIncidentSuggestions())
    setMode('new')
  }

  useEffect(() => {
    if (searchParams.get('report') === '1' && canReport) {
      dispatch(clearIncidentSuggestions())
      setMode('new')
      const next = new URLSearchParams(searchParams)
      next.delete('report')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, canReport, dispatch, setSearchParams])

  // Inline severity quick-edit on the row chips. Refetch the page-1 list
  // so any severity-bound chip filter / counter rehydrates with the new
  // distribution; toast on failure (the chip itself reverts via its
  // internal optimistic-state).
  const updateIncidentSeverity = useCallback(async (incidentId, nextSeverity) => {
    try {
      await IncidentsAPI.update(incidentId, { severity: nextSeverity })
      dispatch(getIncidentsRoutine(buildParams(1)))
      dispatch(getIncidentStatsRoutine(statsParams))
    } catch (e) {
      toast.error('Could not change severity. Please try again.')
      throw e
    }
  }, [dispatch, buildParams, statsParams])

  if (mode === 'new' && !suggestions) {
    return (
      <Page>
        <PageHeader>
          <HeaderLeft>
            <PageTitle><TitleBar />Report incident</PageTitle>
            <PageSubtitle>
              Describe what happened and attach any evidence. Guru will read it back and
              suggest the right severity, category, and remedials.
            </PageSubtitle>
          </HeaderLeft>
        </PageHeader>
        <TitleUnderline />
        <IncidentForm
          availableBPs={effectiveAvailableBPs}
          defaultBpId={filters.building_passport_id}
          onSubmitted={(d) => setPendingForm(d)}
        />
      </Page>
    )
  }

  if (mode === 'review' && suggestions) {
    return (
      <Page>
        <PageHeader>
          <HeaderLeft>
            <PageTitle><TitleBar />Review incident</PageTitle>
            <PageSubtitle>
              Confirm Guru&rsquo;s analysis and pick which suggested remedials to keep before
              filing this incident.
            </PageSubtitle>
          </HeaderLeft>
        </PageHeader>
        <TitleUnderline />
        <IncidentReviewStep
          buildingPassportId={pendingForm?.bpId}
          buildingPassportName={effectiveAvailableBPs.find(b => b.id === pendingForm?.bpId)?.name}
          narrative={pendingForm?.narrative}
          attachmentIds={pendingForm?.attachmentIds || []}
          suggestions={suggestions}
          onCancel={goBackToForm}
        />
      </Page>
    )
  }

  const showSeverityChips = status === 'open' && stats?.bySeverity

  const statCards = [
    {
      key: 'open',
      label: 'Open',
      value: stats?.byStatus?.open ?? 0,
      icon: HourglassEmptyIcon,
      color: '#33becc',
      bg: '#e6f7f9',
      onClick: () => setStatus('open'),
    },
    {
      key: 'critical',
      label: 'Critical',
      value: stats?.bySeverity?.critical ?? 0,
      icon: LocalFireDepartmentIcon,
      color: '#b00020',
      bg: '#fdecea',
      onClick: () => { setStatus('open'); updateParam('severity', 'critical') },
    },
    {
      key: 'resolved',
      label: 'Resolved',
      value: stats?.byStatus?.resolved ?? 0,
      icon: CheckCircleOutlineIcon,
      color: '#059669',
      bg: '#d1fae5',
      onClick: () => setStatus('resolved'),
    },
    {
      key: 'closed',
      label: 'Closed',
      value: stats?.byStatus?.closed ?? 0,
      icon: ArchiveOutlinedIcon,
      color: '#475569',
      bg: '#e2e8f0',
      onClick: () => setStatus('closed'),
    },
  ]

  return (
    <Page>
      <PageHeader>
        <HeaderLeft>
          {returnBp && (
            <BackLink type="button" onClick={() => navigate(`/bp-list/${returnBp}`)}>
              ← Back to Building Passport
            </BackLink>
          )}
          <PageTitle>
            <TitleBar />
            Incidents
          </PageTitle>
          <PageSubtitle>
            Log on-site events as they happen — Guru suggests remedials you can
            triage and convert into tasks.
          </PageSubtitle>
        </HeaderLeft>
        {canReport && (
          <PrimaryButton type="button" onClick={startNewIncident}>
            <AddIcon style={{ fontSize: 18 }} /> Report incident
          </PrimaryButton>
        )}
      </PageHeader>
      <TitleUnderline />

      <GuruTip storageKey='guru_tip_incidents' noun='incident' prompt='Log an incident: ' />

      <StatsRow>
        {statCards.map((s, i) => {
          const Icon = s.icon
          return (
            <StatCard key={s.key} type="button" onClick={s.onClick} $index={i}>
              <StatIcon $color={s.color} $bg={s.bg}><Icon /></StatIcon>
              <StatBody>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </StatBody>
            </StatCard>
          )
        })}
      </StatsRow>

      <FilterBar>
        <FilterTab $active={status === 'open'} onClick={() => setStatus('open')}>
          <span>Open</span>
          <FilterCount $active={status === 'open'}>{stats?.byStatus?.open ?? 0}</FilterCount>
        </FilterTab>
        <FilterTab $active={status === 'resolved'} onClick={() => setStatus('resolved')}>
          <span>Resolved</span>
          <FilterCount $active={status === 'resolved'}>{stats?.byStatus?.resolved ?? 0}</FilterCount>
        </FilterTab>
        <FilterTab $active={status === 'closed'} onClick={() => setStatus('closed')}>
          <span>Closed</span>
          <FilterCount $active={status === 'closed'}>{stats?.byStatus?.closed ?? 0}</FilterCount>
        </FilterTab>
      </FilterBar>

      <RefineRow>
        <TypeSeg>
          {TYPE_OPTIONS.map(t => (
            <TypeSegBtn
              key={t.label}
              type="button"
              $active={(incidentType || null) === t.value}
              onClick={() => updateParam('type', t.value)}
            >
              {t.label}
            </TypeSegBtn>
          ))}
        </TypeSeg>
        <RefineSpacer />
        <QuickToggle
          type="button"
          $active={riddor === 'outstanding'}
          $colour="#d97706"
          $bg="#fef3c7"
          onClick={() => updateParam('riddor', riddor === 'outstanding' ? null : 'outstanding')}
        >
          <GavelRoundedIcon style={{ fontSize: 14 }} /> RIDDOR outstanding
        </QuickToggle>
        <QuickToggle
          type="button"
          $active={mor === 'outstanding'}
          $colour="#c2410c"
          $bg="#ffedd5"
          onClick={() => updateParam('mor', mor === 'outstanding' ? null : 'outstanding')}
        >
          <ApartmentRoundedIcon style={{ fontSize: 14 }} /> MOR outstanding
        </QuickToggle>
        <QuickToggle
          type="button"
          $active={investigation === 'overdue'}
          $colour="#b00020"
          $bg="#fde8e3"
          onClick={() => updateParam('investigation', investigation === 'overdue' ? null : 'overdue')}
        >
          Investigation overdue
        </QuickToggle>
      </RefineRow>

      <Toolbar>
        <SearchWrapper>
          <SearchIcon style={{ fontSize: 18, color: '#94a3b8' }} />
          <SearchField
            type="search"
            placeholder="Search narrative or category…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <ClearSearchBtn
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </ClearSearchBtn>
          )}
        </SearchWrapper>
        <HubScopeFilter
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          lockedBpId={buildingPassportId}
          lockedPortfolioId={portfolioId}
          lockedEstateId={estateId}
        />
      </Toolbar>

      {showSeverityChips && (
        <SeverityRow>
          <SeverityLabel>Severity</SeverityLabel>
          {Object.entries(SEVERITY_META).map(([key, meta]) => {
            const count = stats.bySeverity[key] || 0
            const active = severity === key
            return (
              <SeverityPill
                key={key}
                type="button"
                $active={active}
                $colour={meta.colour}
                $bg={meta.bg}
                onClick={() => toggleSeverity(key)}
              >
                {meta.label}
                <PillCount $active={active}>{count}</PillCount>
              </SeverityPill>
            )
          })}
          {severity && (
            <ClearLink type="button" onClick={() => toggleSeverity(severity)}>Clear</ClearLink>
          )}
        </SeverityRow>
      )}

      {loading && !incidents.length ? (
        <EmptyCard>
          <EmptyIconWrapper $pulse>
            <ReportProblemOutlinedIcon />
          </EmptyIconWrapper>
          <EmptyTitle>Loading incidents…</EmptyTitle>
        </EmptyCard>
      ) : !incidents.length ? (
        <EmptyCard>
          <EmptyIconWrapper>
            <ReportProblemOutlinedIcon />
          </EmptyIconWrapper>
          <EmptyTitle>
            No {(STATUS_META[status]?.label || status).toLowerCase()} incidents
            {severity ? ` at ${severity} severity` : ''}
          </EmptyTitle>
          <EmptyDescription>
            Report an incident to log what happened — the AI will suggest remedials.
          </EmptyDescription>
          {canReport && status === 'open' && (
            <PrimaryButton type="button" onClick={startNewIncident}>
              <AddIcon /> Report incident
            </PrimaryButton>
          )}
        </EmptyCard>
      ) : (
        <>
          <Card>
            {incidents.map(i => (
              <IncidentRow
                key={i.id}
                incident={i}
                onClick={() => setDrawerIncidentId(i.id)}
                onSeverityChange={updateIncidentSeverity}
              />
            ))}
          </Card>
          {hasMore && (
            <LoadMoreRow>
              <LoadMoreButton type="button" onClick={loadMore} disabled={loading}>
                {loading
                  ? 'Loading…'
                  : `Load more (${(meta?.total ?? 0) - incidents.length} remaining)`}
              </LoadMoreButton>
            </LoadMoreRow>
          )}
          {!hasMore && meta?.total > 0 && (
            <ShowingTotal>
              Showing all {meta.total} incident{meta.total === 1 ? '' : 's'}.
            </ShowingTotal>
          )}
        </>
      )}

      <IncidentDetailDrawer
        incidentId={drawerIncidentId}
        open={!!drawerIncidentId}
        onClose={() => setDrawerIncidentId(null)}
        onChanged={() => {
          dispatch(getIncidentsRoutine(buildParams(1)))
          dispatch(getIncidentStatsRoutine(statsParams))
        }}
      />
    </Page>
  )
}

export default IncidentsPage

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 30px 24px;
  @media (max-width: 768px) { padding: 18px 16px; }
`
const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 6px;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    & > button { width: 100%; justify-content: center; }
  }
`
const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`
const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0 0 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  &:hover { color: #0f172a; }
`
const PageTitle = styled.h1`
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`
const TitleBar = styled.span`
  display: inline-block;
  width: 4px;
  height: 20px;
  background: ${({ theme }) => theme.colors.primary};
  margin-right: 10px;
  border-radius: 2px;
`
const PageSubtitle = styled.p`
  margin: 0 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  max-width: 640px;
`
const TitleUnderline = styled.div`
  width: 70px;
  height: 3px;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: 3px;
  margin: 14px 0 22px 14px;
`
const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.16s ease;
  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
  }
`
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`
const StatCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${({ $index }) => $index * 50}ms;
  animation-fill-mode: backwards;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: rgba(0, 0, 0, 0.06);
  }
`
const StatIcon = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  svg { font-size: 22px; color: inherit; }
`
const StatBody = styled.div`
  min-width: 0;
`
const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.haiti};
`
const StatLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  margin-top: 2px;
`
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  margin-bottom: 14px;
  flex-wrap: wrap;
`
const FilterTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.haiti)};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: -1px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`
const FilterCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  line-height: 1.4;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : (theme.colors.grey?.[800] || '#999'))};
  background: ${({ $active, theme }) => ($active ? 'rgba(210, 71, 36, 0.10)' : theme.colors.lightGrey)};
`
const RefineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`
const TypeSeg = styled.div`
  display: inline-flex;
  background: #f3f4f6;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 999px;
  padding: 2px;
`
const TypeSegBtn = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.haiti : '#64748b')};
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  padding: 6px 13px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0,0,0,0.10)' : 'none')};
  transition: color 0.15s;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`
const RefineSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`
const QuickToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $active, $colour }) => ($active ? $colour : '#e5e7eb')};
  background: ${({ $active, $bg }) => ($active ? $bg : '#fff')};
  color: ${({ $active, $colour }) => ($active ? $colour : '#64748b')};
  transition: all 0.15s;
  &:hover { border-color: ${({ $colour }) => $colour}; color: ${({ $colour }) => $colour}; }
`
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`
const SearchWrapper = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.08);
  }
`
const SearchField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: #94a3b8; }
`
const ClearSearchBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: none;
  background: #e2e8f0;
  color: #475569;
  cursor: pointer;
  &:hover { background: #cbd5e1; }
`
const SeverityRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`
const SeverityLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 4px;
`
const SeverityPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $active, $colour }) => ($active ? $colour : 'transparent')};
  background: ${({ $active, $bg }) => ($active ? $bg : '#f3f4f6')};
  color: ${({ $active, $colour }) => ($active ? $colour : '#475569')};
  transition: all 0.15s;
  &:hover { background: ${({ $bg }) => $bg}; color: ${({ $colour }) => $colour}; }
`
const PillCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.6)' : '#e5e7eb')};
  padding: 1px 6px;
  border-radius: 999px;
  line-height: 1.4;
`
const ClearLink = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  &:hover { text-decoration: underline; }
`
const Card = styled.div`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`
const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 14px;
`

const LoadMoreButton = styled.button`
  padding: 8px 18px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const ShowingTotal = styled.div`
  margin-top: 14px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`

const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 24px;
  text-align: center;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`
const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #fbede9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.primary};
  svg { font-size: 40px; }
  ${({ $pulse }) => $pulse && `
    animation: bp-empty-pulse 1.4s ease-in-out infinite;
    @keyframes bp-empty-pulse {
      0%, 100% { opacity: 0.55; transform: scale(1); }
      50%      { opacity: 1;    transform: scale(1.05); }
    }
  `}
`
const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`
const EmptyDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  max-width: 460px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 24px 0;
`
